import { createContext, useContext } from 'react';
import { create } from 'zustand';

export interface ChatSessionState {
  activeConversationId: string | null;
  streamingConvId: string | null;
  /**
   * The active conversation answers 404 — it was unshared under this viewer or deleted. Set once
   * by `useChat`, and cleared by every change of the active conversation, so opening another chat
   * always starts from a clean state.
   */
  activeConversationGone: boolean;
  setActiveConversationId: (id: string | null) => void;
  setStreamingConvId: (id: string | null) => void;
  /** True only on the transition, so the work that belongs to a gone chat runs exactly once. */
  markConversationGone: (id: string) => boolean;
}

/**
 * A fresh chat session: which conversation is open and which one is streaming. Zustand reads via
 * `getState()` are synchronous and always current, which avoids the useState/useRef + useEffect lag
 * that let AbortError microtasks observe a previous-render value. The main chat uses the singleton
 * below; a surface that runs its own chat beside it (the diagram assistant) creates its own, so it
 * never changes what the main chat has open.
 */
export function createChatSessionStore() {
  return create<ChatSessionState>((set, get) => ({
    activeConversationId: null,
    streamingConvId: null,
    activeConversationGone: false,
    setActiveConversationId: (id) =>
      set({ activeConversationId: id, activeConversationGone: false }),
    setStreamingConvId: (id) => set({ streamingConvId: id }),
    /**
     * Ignores a late 404 from a conversation the user has already navigated away from, and a repeat
     * 404 for one already flagged — the initial load and the poll's first tick both reject. Reports
     * whether this call is the one that flipped the flag.
     */
    markConversationGone: (id) => {
      const { activeConversationId, activeConversationGone } = get();
      if (activeConversationId !== id || activeConversationGone) return false;
      set({ activeConversationGone: true });
      return true;
    },
  }));
}

/** The bound store hook `createChatSessionStore` returns. */
export type ChatSessionStore = ReturnType<typeof createChatSessionStore>;

/** The main chat's session, shared by every route and by everything outside a chat subtree. */
export const useChatSessionStore: ChatSessionStore = createChatSessionStore();

/**
 * The session a chat subtree runs against. It defaults to the main chat's singleton, so a subtree
 * with no provider behaves exactly as before; the diagram assistant provides its own.
 */
export const ChatSessionStoreContext =
  createContext<ChatSessionStore>(useChatSessionStore);

/** The session store of the chat subtree this component renders in. */
export function useChatSessionStoreApi(): ChatSessionStore {
  return useContext(ChatSessionStoreContext);
}
